import type { CaseStudy } from '../types';

export const nokia: CaseStudy = {
  id: 'nokia',
  order: 4,
  title: '诺基亚',
  english: 'Nokia',
  category: 'failure',
  era: '1998 — 2014',
  region: '芬兰 / 全球',
  accent: '#746f63',
  hook: '它曾是世界最大的手机制造商，却在智能手机时代迅速失去位置。真正的危险不是没看见变化，而是旧业务太成功，组织无法用新的方式下注。',
  question: '当竞争从硬件转向操作系统和应用生态，为什么一家擅长硬件的公司很难转身？',
  summary:
    '诺基亚的衰落不是单一决策的失败。市场指标、组织激励、渠道资产和技术平台迁移互相强化，让「继续优化现在」在很长时间里看起来比「冒险重建未来」更合理。',
  thesis: '旧优势会变成负债，不是因为优势本身消失，而是因为衡量优势的指标不再对应新的价值来源。',
  quote: '最危险的时刻，往往不是数字变差的时候，而是数字还很好、但价值已经开始迁移的时候。',
  entryNodeIds: ['nokia-metric', 'nokia-platform', 'nokia-org'],
  focusNodeId: 'nokia-platform',
  lensIds: ['network', 'lockin', 'incentives', 'standards'],
  opportunityIds: ['opp-incumbent-bridge'],
  sourceIds: ['nokia-annual-reports', 'microsoft-nokia-2013'],
  facts: [
    {
      text: '诺基亚在 2000 年代长期是全球最大的手机制造商之一；2007 年 iPhone 发布，2008 年 Android 发布，智能手机的竞争重心逐步转向操作系统与应用生态。',
      sourceIds: ['nokia-annual-reports'],
    },
    {
      text: '2013 年，微软宣布以 54.4 亿欧元收购诺基亚的设备与服务业务；交易在 2014 年完成。',
      sourceIds: ['microsoft-nokia-2013'],
    },
    {
      text: '诺基亚并未因此消失：它保留了专利、网络基础设施等业务，后来成为电信设备领域的重要参与者。',
      sourceIds: ['nokia-annual-reports'],
    },
  ],
  nodes: [
    {
      id: 'nokia',
      caseId: 'nokia',
      kind: 'case',
      stage: 0,
      lane: 0,
      title: '诺基亚',
      summary: '功能机时代的王者，在智能机平台战中失去位置。',
      detail:
        '诺基亚擅长硬件、供应链、运营商关系和规模化制造。智能手机把竞争维度从「一部更好的手机」改成「一个操作系统、应用商店和开发者生态」。这不是单点技术的差距，而是价值来源的迁移。',
      boundary: '本案例不是完整的公司史，而是聚焦 2007–2013 年手机业务的结构性困境。',
      evidence: 'fact',
      confidence: 'high',
      sourceIds: ['nokia-annual-reports'],
    },
    {
      id: 'nokia-metric',
      caseId: 'nokia',
      kind: 'mechanism',
      stage: 1,
      lane: -1,
      title: '成功指标：份额与出货量',
      summary: '功能机的高份额、渠道覆盖和硬件利润，让现有路线看起来仍然健康。',
      detail:
        '当组织用市场份额、出货量、平均售价和运营商订单衡量成功，功能机业务会持续获得资源。智能机的早期利润和销量都更小，难以在同一个考核体系里证明自己。指标不是中立的，它决定了哪种未来会被看见。',
      boundary: '如果新业务的战略价值被单独衡量，旧指标未必会阻碍转型。',
      evidence: 'mechanism',
      confidence: 'medium',
      sourceIds: ['nokia-annual-reports'],
    },
    {
      id: 'nokia-platform',
      caseId: 'nokia',
      kind: 'mechanism',
      stage: 1,
      lane: 1,
      title: '平台迁移：竞争从硬件转向生态',
      summary: '操作系统、应用商店和开发者，形成新的双边网络效应。',
      detail:
        '智能手机的价值越来越取决于应用数量和开发者意愿。更多用户吸引开发者，更多应用又吸引用户。硬件参数仍然重要，但单靠更好的摄像头或信号，无法替代一个持续扩张的应用生态。',
      boundary: '在应用需求弱、企业定制或低联网市场，硬件与渠道仍可能决定胜负。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['nokia-annual-reports'],
    },
    {
      id: 'nokia-org',
      caseId: 'nokia',
      kind: 'mechanism',
      stage: 2,
      lane: -1,
      title: '组织惯性：内部平台互相消耗',
      summary: '塞班、MeeGo 等路线之间的协调成本，拖慢了统一下注的速度。',
      detail:
        '大公司同时押注多个平台并不罕见，难点是资源、责任和节奏能否统一。当既有业务仍在贡献收入，新平台很难获得不受短期考核约束的空间；内部竞争也会让开发者与供应链收到混乱信号。',
      boundary: '这段组织史存在多种叙述；「内耗导致失败」是解释，不应替代对产品和市场时机的具体分析。',
      evidence: 'inference',
      confidence: 'medium',
      sourceIds: ['nokia-annual-reports'],
    },
    {
      id: 'nokia-assets',
      caseId: 'nokia',
      kind: 'mechanism',
      stage: 2,
      lane: 1,
      title: '旧资产：渠道与制造不再是胜负手',
      summary: '运营商关系、低成本制造和硬件供应链仍然有价值，但无法替代软件生态。',
      detail:
        '诺基亚的资产在功能机时代极其强大：规模化制造、全球渠道、运营商关系、品牌认知。但智能机的利润向软件、应用分发和生态控制转移，旧资产仍能降低硬件成本，却不能自动买来开发者。',
      boundary: '在价格敏感、应用生态弱的市场，旧资产仍能转化为份额和利润。',
      evidence: 'inference',
      confidence: 'high',
      sourceIds: ['nokia-annual-reports'],
    },
    {
      id: 'nokia-condition',
      caseId: 'nokia',
      kind: 'condition',
      stage: 2,
      lane: 0,
      title: '前提：触屏供应链与开发者迁移',
      summary: '触屏、应用商店、移动互联网和开发者工具成熟，才让平台竞争成为主战场。',
      detail:
        '如果触屏成本高、移动网络慢、支付与开发者分成机制不成熟，智能机的生态优势不会这么快显现。诺基亚面对的不是一款产品，而是一组同时成熟的外部条件。',
      boundary: '这些条件在不同市场成熟速度不同，因此诺基亚的衰落也不是同步发生的。',
      evidence: 'fact',
      confidence: 'high',
      sourceIds: ['nokia-annual-reports'],
    },
    {
      id: 'nokia-outcome',
      caseId: 'nokia',
      kind: 'outcome',
      stage: 3,
      lane: 0,
      title: '结果：手机业务出售，专利与网络留存',
      summary: '智能机份额坍塌，设备业务被出售；但诺基亚没有整体消失。',
      detail:
        '诺基亚把设备与服务业务卖给微软，随后将重心转向网络基础设施与专利授权。这提醒我们：一家公司的某一业务失败，不等于整个公司归零；资产可以重组，能力可以迁移。',
      boundary: '业务出售是结果，不是单一原因；把它简单归因于「没做触屏」会掩盖组织与生态问题。',
      evidence: 'fact',
      confidence: 'high',
      sourceIds: ['microsoft-nokia-2013', 'nokia-annual-reports'],
    },
    {
      id: 'nokia-hindsight',
      caseId: 'nokia',
      kind: 'risk',
      stage: 4,
      lane: -1,
      title: '脆弱处：后见之明叙事',
      summary: '「诺基亚没看见智能机」是最流行、也最偷懒的解释。',
      detail:
        '诺基亚并非没有触屏原型、互联网战略或软件投入。更接近事实的问题是：它看见了，却无法在旧业务的考核、组织与渠道中，以足够快的速度和统一的决心完成迁移。把失败归为「愚蠢」会让我们学不到东西。',
      boundary: '这也不是为管理层开脱；具体的产品延迟、平台选择和执行失误仍然需要被追问。',
      evidence: 'opinion',
      confidence: 'medium',
      sourceIds: ['nokia-annual-reports'],
    },
    {
      id: 'nokia-opp',
      caseId: 'nokia',
      kind: 'opportunity',
      stage: 4,
      lane: 1,
      title: '机会透镜：卖「转身能力」',
      summary: '成熟企业的盲区，往往不是不知道新技术，而是无法用旧组织完成新业务。',
      detail:
        '对创业者，机会可能是为在位企业提供独立的新业务单元、外部孵化、人才与供应链接口；对个人，机会是成为「旧组织与新能力之间的翻译」。不要只卖技术，要卖一条不伤害旧业务的下注路径。',
      boundary: '大企业采购慢、政治复杂、责任难界定；如果没有高层赞助和清晰的成功标准，外部团队会被旧指标吞掉。',
      evidence: 'inference',
      confidence: 'medium',
      sourceIds: ['nokia-annual-reports'],
    },
  ],
  edges: [
    {
      id: 'nokia-e1',
      caseId: 'nokia',
      from: 'nokia',
      to: 'nokia-metric',
      label: '旧业务定义成功',
      explanation: '功能机的份额、出货与渠道覆盖，成为资源配置的主要依据。',
      tone: 'cause',
    },
    {
      id: 'nokia-e2',
      caseId: 'nokia',
      from: 'nokia',
      to: 'nokia-platform',
      label: '竞争维度迁移',
      explanation: '智能机把价值从硬件参数移向操作系统、应用与开发者生态。',
      tone: 'cause',
    },
    {
      id: 'nokia-e3',
      caseId: 'nokia',
      from: 'nokia-metric',
      to: 'nokia-org',
      label: '指标保护旧路线',
      explanation: '新平台难以在短期销量和利润上证明自己，资源与决策速度被旧业务牵制。',
      tone: 'cause',
    },
    {
      id: 'nokia-e4',
      caseId: 'nokia',
      from: 'nokia-platform',
      to: 'nokia-org',
      label: '生态需要统一下注',
      explanation: '开发者与供应链需要清晰信号，多平台并行会提高协调成本。',
      tone: 'cause',
    },
    {
      id: 'nokia-e5',
      caseId: 'nokia',
      from: 'nokia-platform',
      to: 'nokia-assets',
      label: '旧资产贬值',
      explanation: '渠道、制造和运营商关系仍有用，但不再是应用生态的入场券。',
      tone: 'cause',
    },
    {
      id: 'nokia-e6',
      caseId: 'nokia',
      from: 'nokia-org',
      to: 'nokia-outcome',
      label: '执行速度落后',
      explanation: '路线摇摆与组织协调成本，让产品节奏落后于生态竞争的要求。',
      tone: 'cause',
    },
    {
      id: 'nokia-e7',
      caseId: 'nokia',
      from: 'nokia-assets',
      to: 'nokia-outcome',
      label: '硬件优势不足以翻盘',
      explanation: '更好的硬件无法弥补应用生态和开发者迁移的差距。',
      tone: 'cause',
    },
    {
      id: 'nokia-e8',
      caseId: 'nokia',
      from: 'nokia-condition',
      to: 'nokia-outcome',
      label: '外部条件成熟',
      explanation: '触屏、移动网络、应用商店与开发者工具同时成熟，加速了平台替代。',
      tone: 'enable',
    },
    {
      id: 'nokia-e9',
      caseId: 'nokia',
      from: 'nokia-outcome',
      to: 'nokia-hindsight',
      label: '结果被简化为故事',
      explanation: '失败之后，复杂的产品与组织过程容易被压缩成「没看见趋势」的单句。',
      tone: 'risk',
    },
    {
      id: 'nokia-e10',
      caseId: 'nokia',
      from: 'nokia-outcome',
      to: 'nokia-opp',
      label: '转身能力稀缺',
      explanation: '在位企业需要一条不伤害旧业务的新下注路径，外部服务因此有空间。',
      tone: 'cause',
    },
  ],
};
